import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { reasonCodeService, ReasonCodeDto, UpdateReasonCodeRequest } from '../../../../../services/reasonCodeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { ReasonCodeSidePanel } from './ReasonCodeSidePanel';
import { ReasonCodeCreateModal } from './ReasonCodeCreateModal';

interface ReasonCodeDataTableProps {
  refreshTrigger?: number;
}

export const ReasonCodeDataTable: React.FC<ReasonCodeDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  // Localized reason type options + label resolver
  const reasonTypeOptions = useMemo(() => [
    { value: 1, label: t('manager.modules.adminMovement.reasonCode.typeWarehouse', {}, 'Årsakskoder lager') },
    { value: 2, label: t('manager.modules.adminMovement.reasonCode.typeDistribution', {}, 'Årsakskoder distribusjon') }
  ], [t]);

  const getReasonTypeLabel = useCallback((value: number | null | undefined): string => {
    if (value == null) return '-';
    const option = reasonTypeOptions.find(o => o.value === value);
    return option ? option.label : String(value);
  }, [reasonTypeOptions]);

  // Core data state
  const [data, setData] = useState<ReasonCodeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing
  const inlineEditConfig = useInlineEdit<ReasonCodeDto>({
    getRowId: (row) => row.id,
    onSave: async (_originalRow, editedData) => {
      const newReasonCodeNo = editedData.reasonCodeNo != null ? Number(editedData.reasonCodeNo) : undefined;
      const request: UpdateReasonCodeRequest = {
        reasonCodeNo: newReasonCodeNo !== _originalRow.reasonCodeNo ? newReasonCodeNo : undefined,
        reasonDescription: (editedData.reasonDescription as string) || null,
        reasonType: editedData.reasonType as number | null | undefined,
        externalID: (editedData.externalID as string) || null
      };
      await reasonCodeService.update(
        _originalRow.companyNo,
        _originalRow.reasonCodeNo,
        request
      );
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminMovement.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const reasonCodeData = await reasonCodeService.getCurrent().catch(() =>
        reasonCodeService.getAll().catch(() => [])
      );
      setData(reasonCodeData);
    } catch (err: any) {
      console.error('Error loading reason code data:', err);
      setError(t('manager.modules.adminMovement.reasonCode.loadError', {}, 'Kunne ikke laste årsakskoder. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  // Column factory
  const columnFactory = createColumnFactory<ReasonCodeDto>();

  // Column definitions
  const columns: ColumnDefinition<ReasonCodeDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('reasonCodeNo', 'reasonCodeNo', t('manager.modules.adminMovement.reasonCode.label', {}, 'Årsakskode'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('reasonDescription', 'reasonDescription', t('manager.modules.adminMovement.fields.description', {}, 'Beskrivelse'), {
      minWidth: 250,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('reasonType', 'reasonType', t('manager.modules.adminMovement.reasonCode.fields.reasonType', {}, 'Årsakstype'), {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true,
      editType: 'select',
      editOptions: reasonTypeOptions,
      formatter: (value: any) => getReasonTypeLabel(value)
    }),

    columnFactory.createTextColumn('externalID', 'externalID', t('manager.modules.adminMovement.reasonCode.fields.externalID', {}, 'Ekstern ID'), {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [t, reasonTypeOptions, getReasonTypeLabel]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminMovement.reasonCode.selectAtLeastOne', {}, 'Velg minst én årsakskode for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Table actions
  const tableActions: TableAction<ReasonCodeDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminMovement.reasonCode.createTitle', {}, 'Ny årsakskode'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminMovement.maintenance', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  // Build selected rows map for SidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'reason-codes-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <ReasonCodeSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ReasonCodeCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
